import axios from 'axios';
import { getApiErrorFromResponse } from '@/models/entities/api/api-error';
import ApiResponse from '@/models/entities/api/api-response';
import ResendVerificationCodeResponse from '@/models/entities/api/authorization-resource/resend-verification-code-response';
import { parseVerifyEmailResponse } from '@/models/entities/api/authorization-resource/verify-email-response-type-guards';
import VerifyEmailRequest from '@/models/entities/api/authorization-resource/verify-email-request';
import VerifyCustomerRequest from '@/models/entities/api/authorization-resource/verify-customer-request';
import ResendVerificationCodeRequest from '@/models/entities/api/authorization-resource/resend-verification-code-request';
import ApiLocked from '@/models/entities/api/api-locked';

export type VerificationLockStateListener = (verificationLockState: boolean) => void;

/**
 * Represents /auth methods in the WebCart API.
 *
 * `verifyEmail`, `resendVerificationCode`, and `verifyCustomer` all share a lock. No more than one
 * of these requests may be active at once.
 */
export default class AuthorizationResource {
  private verificationLock: boolean;

  private readonly verificationLockStateListeners: Record<string, VerificationLockStateListener>;

  constructor() {
    this.verificationLock = false;
    this.verificationLockStateListeners = {};
  }

  registerVerificationLockStateListener(name: string, listener: VerificationLockStateListener) {
    if (this.verificationLockStateListeners[name]) {
      console.debug(`No need to add a duplicate VerificationLockStateListener with name ${name}`);
      return;
    }
    console.debug(`Adding VerificationLockStateListener with name: ${name}`);
    this.verificationLockStateListeners[name] = listener;
  }

  unregisterVerificationLockStateListener(name: string) {
    console.debug(`Removing VerificationLockStateListener with name ${name}`);
    delete this.verificationLockStateListeners[name];
  }

  takeVerificationLock() {
    this.verificationLock = true;
    for (const listener of Object.values(this.verificationLockStateListeners)) {
      listener(true);
    }
  }

  releaseVerificationLock() {
    this.verificationLock = false;
    for (const listener of Object.values(this.verificationLockStateListeners)) {
      listener(false);
    }
  }

  async verifyEmail(request: VerifyEmailRequest) {
    if (this.verificationLock) {
      return new ApiLocked();
    }
    this.takeVerificationLock();
    try {
      const response = await axios.post('api/auth/verifyEmail', request);
      return parseVerifyEmailResponse(response.data);
    } catch (error) {
      return getApiErrorFromResponse(error);
    } finally {
      this.releaseVerificationLock();
    }
  }

  async resendVerificationCode(request: ResendVerificationCodeRequest) {
    if (this.verificationLock) {
      return new ApiLocked();
    }
    this.takeVerificationLock();
    try {
      const response = await axios.post('api/auth/verify/resend', request);
      return new ResendVerificationCodeResponse(response.data);
    } catch (error) {
      return getApiErrorFromResponse(error);
    } finally {
      this.releaseVerificationLock();
    }
  }

  async verifyCustomer(request: VerifyCustomerRequest) {
    if (this.verificationLock) {
      return new ApiLocked();
    }
    this.takeVerificationLock();
    try {
      const response = await axios.post('api/auth/verify', request);
      return new ApiResponse(response.data);
    } catch (error) {
      return getApiErrorFromResponse(error);
    } finally {
      this.releaseVerificationLock();
    }
  }
}
